import * as client from "_graphql-types";
import * as types from "./types";

import { filterSortPage } from "../DataProvider";
import {
  COMPANY_BUSINESS_OBJECT_ENUM,
  DEAL_BUSINESS_OBJECT_ENUM,
  INVESTMENT_BUSINESS_OBJECT_ENUM,
} from "./businessObjectEnum";

type Sdk = ReturnType<typeof client.getSdk>;

// Flatten the React-Admin array-form shape into the GraphQL NoteEntityAssignmentInput.
// Each row has a businessObjectEnumId plus a nested entity (investment/company/deal)
// picked via EntityInput.
function toEntityAssignmentsInput(data: any) {
  let rows = Array.isArray(data?.entityAssignments)
    ? data.entityAssignments
    : [];
  if (rows.length === 0 && data?.businessObjectEnumId) {
    rows = [data];
  }
  return rows
    .map((row: any) => {
      const kind = row?.businessObjectEnumId;
      let entityId: number | undefined;
      if (kind === INVESTMENT_BUSINESS_OBJECT_ENUM) {
        entityId = row?.investment?.id ?? row?.entityId;
      } else if (kind === COMPANY_BUSINESS_OBJECT_ENUM) {
        entityId = row?.company?.id ?? row?.entityId;
      } else if (kind === DEAL_BUSINESS_OBJECT_ENUM) {
        entityId = row?.deal?.id ?? row?.entityId;
      }
      if (typeof entityId !== "number" || !kind) return null;
      return { businessObjectEnumId: kind, entityId: Number(entityId) };
    })
    .filter(
      (r: any): r is { businessObjectEnumId: number; entityId: number } => !!r
    );
}

// Seed the array-form shape from a loaded note's entityAssignments, so
// the Edit form can render one row per existing attachment.
function fromEntityAssignments(
  entityAssignments:
    | Array<{
        businessObjectTypeEnumId: number;
        entityId: number;
        entityName?: string | null;
      }>
    | null
    | undefined
) {
  return (entityAssignments ?? []).map(a => {
    const base = {
      businessObjectEnumId: a.businessObjectTypeEnumId,
      entityId: a.entityId,
    };
    switch (a.businessObjectTypeEnumId) {
      case INVESTMENT_BUSINESS_OBJECT_ENUM:
        return {
          ...base,
          investment: { id: a.entityId, name: a.entityName ?? "" },
        };
      case COMPANY_BUSINESS_OBJECT_ENUM:
        return {
          ...base,
          company: { id: a.entityId, name: a.entityName ?? "" },
        };
      case DEAL_BUSINESS_OBJECT_ENUM:
        return {
          ...base,
          deal: { id: a.entityId, name: a.entityName ?? "" },
        };
      default:
        return base;
    }
  });
}

/*********************************************************************/
// Data Provider Functions
/*********************************************************************/

/*********************************************************************/
async function getList(
  sdk: Sdk,
  params: types.GetListParams<client.NotesSortEnum>
) {
  const { pagination, sort, filter } = params;

  const {
    notesList: { items: data, total },
  } = await sdk.getListNotes(filterSortPage({ filter, sort, pagination }));

  return {
    data,
    total,
  };
}

/*********************************************************************/
async function getOne(sdk: Sdk, params: types.GetOneParams) {
  const id = Number(params.id);
  const { notes: data } = await sdk.getOneNotes({
    id,
  });
  if (data) {
    (data as any).entityAssignments = fromEntityAssignments(
      (data as any).entityAssignments
    );
  }
  return {
    data,
  };
}

/*********************************************************************/
async function getMany(sdk: Sdk, params: types.GetManyParams) {
  const ids = params.ids.map(id => Number(id));

  const { notesMany: data } = await sdk.getManyNotes({
    ids,
  });

  return {
    data,
  };
}

/****************************************************************** */
async function create(
  sdk: Sdk,
  params: types.CreateParams<client.MutationAddNoteArgs["input"]>
) {
  const { data } = params;

  const input: client.MutationAddNoteArgs["input"] = {
    noteMetaId: types.required(data, "noteMetaId"),
    noteDate: data.noteDate,
    text: data.text,
    accessLevelId: data.accessLevelId ?? 2,
    entityAssignments: toEntityAssignmentsInput(data),
    notifyTeam: data.notifyTeam,
  };
  const {
    addNote: { id },
  } = await sdk.addNote({
    input,
  });
  return await getOne(sdk, { id: id! });
}

/****************************************************************** */
async function update(
  sdk: Sdk,
  params: types.UpdateParams<client.MutationAddNoteArgs["input"]>
) {
  const id = Number(params.id);
  const { data } = params;
  const input: client.UpdateNoteMutationVariables["input"] = {
    noteMetaId: types.required(data, "noteMetaId"),
    noteDate: data.noteDate,
    text: data.text,
    accessLevelId: data.accessLevelId ?? 2,
    entityAssignments: toEntityAssignmentsInput(data),
  };
  await sdk.updateNote({ id, input });

  return await getOne(sdk, { id });
}
/****************************************************************** */
async function getManyReference(
  sdk: Sdk,
  params: types.GetManyReferenceParams<client.NotesSortEnum>
) {
  const id = Number(params.id);
  const {
    pagination: { page, perPage },
    sort: { field, order },
    filter,
    target,
  } = params;

  const {
    notesList: { items: data, total },
  } = await sdk.getListNotes({
    filter: { ...filter, [target]: id },
    page: {
      offset: (page - 1) * perPage,
      limit: perPage,
    },
    sort: [{ field, order }],
  });

  return {
    data,
    total,
  };
}

export const notes = types.dataProvider({
  getList,
  getOne,
  getMany,
  create,
  update,
  getManyReference,
});
